import { useMemo } from 'react'

import { calculateSizeInLots } from '@pages/Trade/utils/helpers'

import type { DxtradeInstrumentMap } from './useDxtradeInstruments'
import type { PendingOrderWithTPSL } from './useDxtradePendingOrders'

export type PendingOrderWithLots = PendingOrderWithTPSL & {
  sizeLots?: number
  description?: string
  instrumentCurrency?: string | null
}

const getPendingOrderQuantity = (order: PendingOrderWithTPSL): number | undefined => {
  const leg = order.legs?.[0]
  return typeof leg?.quantity === 'number' ? leg.quantity : undefined
}

export const usePendingOrdersWithLots = (
  orders: PendingOrderWithTPSL[] | undefined,
  instruments: DxtradeInstrumentMap
): PendingOrderWithLots[] => {
  return useMemo(() => {
    if (!orders?.length) {
      return orders ?? []
    }

    return orders.map<PendingOrderWithLots>((order) => {
      const symbol = order.instrument
      if (!symbol) {
        return order
      }
      const instrument = instruments[symbol]
      const instrumentCurrency = instrument?.currency ?? null
      const lotSize = instrument?.lotSize
      if (!lotSize) {
        return { ...order, instrumentCurrency }
      }
      const quantity = getPendingOrderQuantity(order)
      if (quantity == null) {
        return { ...order, instrumentCurrency }
      }
      const quantityIncrement = instrument?.quantityIncrement
      const computedSize = calculateSizeInLots(quantity, lotSize, quantityIncrement)
      const description = instruments[symbol]?.description ?? undefined

      return {
        ...order,
        sizeLots: computedSize,
        description,
        instrumentCurrency,
      }
    })
  }, [orders, instruments])
}
